/**
 * LE/CD (TRID Disclosures) API
 *
 * Contract: LE_CD_API_CONTRACT.md — frozen at the Stage A handoff.
 *
 * Three things about this API that are easy to get wrong, so they are handled
 * here once rather than in six tabs:
 *
 * 1. **Every response is enveloped** as `{status, message, data}`. Unwrapped in
 *    `transformResponse` so components only ever see `data`.
 * 2. **Money is a string** — `"286.40"`, never `286.4`. These are `Decimal`
 *    server-side and `286.40` round-trips through a JS float as
 *    `286.39999999999998`. Never `parseFloat` them; see `disclosureFormat.js`.
 * 3. **One tag for the whole feature.** Drafting, releasing or discarding a
 *    disclosure changes what *every* tab shows — the ledger gains a version,
 *    baselines move, the cure recomputes, rules change state. Invalidating a
 *    single `Disclosure` tag means all six tabs re-derive from one truth
 *    instead of drifting apart.
 *
 * Mutations that change the draft state also invalidate the fees worksheet,
 * because the worksheet lock depends on whether a draft is open.
 */
import { getHeaders } from 'auth/auth';
import { api } from 'constants/constant';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../baseQuery';
import { adminFeesworksheetAPI } from './feesworksheet';

const base = (loanId) => `/b2b/loan/applications/${loanId}/disclosures`;

/** Unwrap `{status, message, data}`. Tolerates an already-bare body. */
const unwrap = (response) =>
  response && typeof response === 'object' && 'data' in response
    ? response.data
    : response;

/**
 * Releasing or drafting changes whether the fee worksheet is editable, and the
 * worksheet lives in a different RTK Query slice. Tag invalidation does not
 * cross slices, so it has to be dispatched explicitly — same approach as
 * `_invalidateDocsOnSuccess` in `redux/services/borrower-profile.js`.
 *
 * Recording a circumstance is on this list too, and not for the lock: the
 * figures an officer states go onto the fee lines themselves wherever the
 * worksheet is unlocked, so a Fees tab left open would keep showing the amount
 * that was just replaced.
 */
const invalidateFeesOnSuccess = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
    dispatch(adminFeesworksheetAPI.util.invalidateTags(['getFees']));
  } catch {
    // Mutation failed; the worksheet's cached state is still accurate.
  }
};

export const disclosuresAPI = createApi({
  reducerPath: 'Disclosures',
  tagTypes: ['Disclosure'],
  baseQuery,
  endpoints: (builder) => ({
    // ── reads ────────────────────────────────────────────────────────────
    getDisclosureContext: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/context`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    getAutomation: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/automation`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    getLedger: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/ledger`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * Every projection in one request, from one server-side snapshot.
     *
     * The tab strip needs four of them for its badge counts, which used to be
     * four requests — and each one rebuilds a snapshot, four queries and close
     * to a second of round trips against a remote database. The projections
     * themselves are pure, so asking for all six costs exactly what asking for
     * one did.
     *
     * Carries the same `Disclosure` tag as the individual reads, so a mutation
     * still refreshes this and the tabs together and none of them can be left
     * showing a figure the others have moved past.
     */
    getOverview: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/overview`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * The change-of-circumstance register: `{summary, records}`.
     *
     * `summary` carries a count per status plus `blocks_cd` and `next_due_by`,
     * so the tab can state what is outstanding without walking the records.
     *
     * Rejected and withdrawn records come back with everything else, by design
     * — a register that hides its refusals is not a control.
     */
    getCocRegister: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/coc`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * One record, with the three things the register list leaves out: the
     * deltas it resets, its evidence, and the server's own scope warnings.
     */
    getCocRecord: builder.query({
      query: ({ loanId, cocId }) => ({
        url: `${base(loanId)}/coc/${cocId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * Whether a Closing Disclosure may be drafted, as fourteen checks grouped
     * by who owns them.
     *
     * Three states, and the third is the point: `unknown` means the server
     * declined to evaluate the check, which is not a pass. Only checks whose
     * `severity` is `blocking` and whose `state` is `failed` count towards
     * `blocking_count` — an `unknown` never blocks.
     */
    getCdReadiness: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/cd-readiness`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * Everything the COC form needs to render itself: the reason codes with
     * their own labels and availability, the prong options, where a record
     * would route, and the lines it could cover.
     *
     * The labels are the reason this is fetched rather than hardcoded — the
     * client-side map this replaced had A and B the wrong way round.
     */
    getCocFormContext: builder.query({
      /* `editing` names the record the form is for, so the already-covered
         warnings leave that record's own charges alone. */
      query: ({ loanId, editing }) => ({
        url: `${base(loanId)}/coc/form-context`,
        method: 'GET',
        params: editing ? { editing } : undefined,
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * What this file needs done next, from a circumstance to a released version.
     *
     * Five stages, each carrying its own state, the sentence that explains it,
     * and the actions it permits. Composed server-side from one snapshot, so it
     * cannot disagree with the register or the readiness panel about whether a
     * record is pending.
     *
     * **Every date and every gate is read from here, never from a chat
     * artifact.** The artifact is persisted and replayed on history load, so a
     * plan reopened days later may name a route that has since closed —
     * `revised_le_cutoff` moves whenever the closing date does. Under the
     * `Disclosure` tag, so acting on a stage refreshes the plan and the register
     * together.
     */
    getCocPlaybook: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/coc/playbook`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    getTolerance: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/tolerance`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * Credit the outstanding tolerance cure to the borrower.
     *
     * No amount is sent. The cure is whatever the server's tolerance test makes
     * it — an amount the client could pass is an amount the client could get
     * wrong, and a short cure is indistinguishable from a paid one afterwards.
     *
     * Idempotent: applying again after a fee moves restates the same Section L
     * line rather than adding a second credit.
     */
    applyToleranceCure: builder.mutation({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/tolerance/cure`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      // The credit changes the worksheet, the comparison grid and whether a CD
      // can issue — everything this tag covers.
      invalidatesTags: ['Disclosure'],
    }),

    getTridChecks: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/trid-checks`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    getComparison: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/comparison`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * What is about to be frozen, and what looks wrong about it.
     *
     * Read immediately before releasing. `requires_acknowledgement` is the set
     * of warning codes the release call must echo back — the server compares
     * whole sets, so a fee edited in another tab between reading this and
     * clicking release produces a 409 rather than a silent pass.
     */
    getReleasePreflight: builder.query({
      query: ({ loanId, ref }) => ({
        url: `${base(loanId)}/${ref}/release-preflight`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * The disclosure as its prescribed CFPB form — H-24 over 3 pages, H-25 over
     * 5. The PDF endpoint renders this same object, which is why the Document
     * tab reads it rather than assembling pages from `/comparison`: two
     * renderers cannot disagree about a figure neither of them works out.
     *
     * Every total arrives as a string already summed server-side. Nothing here
     * adds money.
     */
    getDisclosureForm: builder.query({
      query: ({ loanId, ref }) => ({
        url: `${base(loanId)}/${ref}/form`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * Everyone on the loan right now, off the working fee worksheet.
     *
     * The per-version query below cannot answer before there is a version, and
     * that is exactly when a loan officer is drafting the Initial LE: naming
     * payees and wanting to see who is on the file.
     *
     * `Disclosure` is the load-bearing part. Every fee mutation runs
     * `invalidateDisclosuresOnSuccess`, so pressing Save on the worksheet
     * invalidates this entry and a typed payee appears here without a reload.
     * Sharing the tag with the per-version query is deliberate: a worksheet
     * edit changes both answers.
     */
    getWorkingContacts: builder.query({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/contacts`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * The page-5 contact block, plus the vendor and payee records behind it.
     *
     * Keyed by `ref` rather than read from the loan's live vendor list because
     * these froze when the version was issued: the settlement agent named on
     * CD-1 is whoever was named that day, even after the file is reassigned.
     *
     * Every matrix cell arrives as `{value, source}` — `source` distinguishes a
     * real loan value from a screenshot placeholder, and the UI has to keep
     * them apart on screen.
     */
    getDisclosureContacts: builder.query({
      query: ({ loanId, ref }) => ({
        url: `${base(loanId)}/${ref}/contacts`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    /**
     * A contact typed into the modal, kept on the loan. Invalidates the same
     * tag the two contact reads provide, so the directory refetches and the
     * new row arrives from the server in the shape every other row has.
     */
    addLoanContact: builder.mutation({
      query: ({ loanId, contact }) => ({
        url: `${base(loanId)}/contacts`,
        method: 'POST',
        headers: getHeaders(),
        body: contact,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /** Takes a typed-in contact off the working view; versions already issued keep it. */
    removeLoanContact: builder.mutation({
      query: ({ loanId, contactId }) => ({
        url: `${base(loanId)}/contacts/${contactId}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /**
     * Where the signature request has got to. `refresh` re-reads it from the
     * vendor, and is also the call that notices a signature and writes the real
     * receipt date — so polling it keeps the TRID clocks honest.
     */
    getEsignStatus: builder.query({
      query: ({ loanId, ref, refresh = false }) => ({
        url: `${base(loanId)}/${ref}/esign${refresh ? '?refresh=true' : ''}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Disclosure'],
    }),

    sendForEsign: builder.mutation({
      query: ({ loanId, ref, ...body }) => ({
        url: `${base(loanId)}/${ref}/esign`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    withdrawEsign: builder.mutation({
      query: ({ loanId, ref, reason }) => ({
        url: `${base(loanId)}/${ref}/esign${
          reason ? `?reason=${encodeURIComponent(reason)}` : ''
        }`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /**
     * Walks the mock envelope one step. Dev-only server-side — 404 anywhere
     * else — because reaching `completed` writes a receipt date that moves
     * every TRID deadline on the file.
     */
    advanceEsign: builder.mutation({
      query: ({ loanId, ref }) => ({
        url: `${base(loanId)}/${ref}/esign/advance`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    // ── context ──────────────────────────────────────────────────────────
    updateDisclosureContext: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${base(loanId)}/context`,
        method: 'PATCH',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    // ── drafting ─────────────────────────────────────────────────────────
    /** R-01's action — auto-draft the Initial LE once all six elements land. */
    draftInitialLe: builder.mutation({
      query: ({ loanId }) => ({
        url: `${base(loanId)}/automation/draft-initial-le`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /** kind: 'LE' | 'CD'. `covers` scopes which lines a circumstance re-baselines. */
    draftDisclosure: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${base(loanId)}/draft`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /**
     * Irreversible. Freezes every working figure onto this disclosure; a
     * database trigger refuses any later edit. `has_prepayment_penalty` is
     * tri-state — omit it to keep whatever the previous disclosure recorded.
     */
    releaseDisclosure: builder.mutation({
      query: ({ loanId, ref, ...body }) => ({
        url: `${base(loanId)}/${ref}/release`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /** Only drafts can be discarded. Issued disclosures are permanent. */
    discardDisclosure: builder.mutation({
      query: ({ loanId, ref }) => ({
        url: `${base(loanId)}/${ref}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /** Actual receipt supersedes the presumed mailbox-rule date. */
    confirmReceipt: builder.mutation({
      query: ({ loanId, ref, received_on }) => ({
        url: `${base(loanId)}/${ref}/receipt`,
        method: 'POST',
        headers: getHeaders(),
        body: { received_on },
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /** File the rendered PDF into the loan's Documents tab. Idempotent. */
    archiveDisclosure: builder.mutation({
      query: ({ loanId, ref }) => ({
        url: `${base(loanId)}/${ref}/archive`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    // ── events ───────────────────────────────────────────────────────────
    /** Diff a live Lodestar quote against the last disclosed figures (R-05). */
    detectSettlementRequote: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${base(loanId)}/events/settlement-requote`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /** Run after a pricing autofill has written new figures to the worksheet. */
    detectPricingChange: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${base(loanId)}/events/pricing-change`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /**
     * Record a circumstance by hand.
     *
     * The register's three detectors cover a rate lock, a settlement re-quote
     * and a delivered invoice. Codes **B** and **C** — eligibility changed, the
     * borrower asked for a revision — have no detector and never will: they are
     * things a person notices. Without this there was no way to record one, so
     * no revised Loan Estimate could ever be drafted for either.
     *
     * `recorded_at` is deliberately not accepted. It is the server's stamp, and
     * its distance from `learned_on` is the only thing that makes backdating
     * visible.
     */
    recordCoc: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${base(loanId)}/coc`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /**
     * Attach a document already filed against this loan, by reference.
     *
     * Never an upload: a service order's deliverable is already a document, and
     * re-uploading it would leave two records of one artifact. Which is also
     * why `attachable_documents` marks each one's origin — a deliverable that
     * arrived with the invoice is stronger evidence than something typed in.
     */
    attachCocEvidence: builder.mutation({
      query: ({ loanId, cocId, documentId, kind }) => ({
        url: `${base(loanId)}/coc/${cocId}/evidence`,
        method: 'POST',
        headers: getHeaders(),
        body: { document_id: documentId, ...(kind ? { kind } : {}) },
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /**
     * Detach it. The document itself is untouched — and while it is attached
     * the Documents tab refuses to delete it, because `coc_evidence.document_id`
     * is ON DELETE RESTRICT.
     */
    detachCocEvidence: builder.mutation({
      query: ({ loanId, cocId, evidenceId }) => ({
        url: `${base(loanId)}/coc/${cocId}/evidence/${evidenceId}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /**
     * Add approved records to a draft already open.
     *
     * One version can carry several circumstances and `POST /draft` takes a
     * list — but only at creation, which is not when the second one arrives.
     * The server recomputes `covers` from the whole set, because that column
     * is what decides which lines re-baseline.
     */
    attachCocsToDraft: builder.mutation({
      query: ({ loanId, ref, cocIds }) => ({
        url: `${base(loanId)}/${ref}/coc`,
        method: 'POST',
        headers: getHeaders(),
        body: { coc_ids: cocIds },
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /** Take one back off, returning it to the register unused. */
    detachCocFromDraft: builder.mutation({
      query: ({ loanId, ref, cocId }) => ({
        url: `${base(loanId)}/${ref}/coc/${cocId}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    /**
     * The money decision. A baseline moves on the strength of this, which is
     * why the server refuses it without an attributable user.
     */
    approveCoc: builder.mutation({
      query: ({ loanId, cocId, ...body }) => ({
        url: `${base(loanId)}/coc/${cocId}/approve`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /**
     * Refused, and kept. The refusal is the evidence the control was applied,
     * so the reason is required rather than optional.
     */
    rejectCoc: builder.mutation({
      query: ({ loanId, cocId, reason }) => ({
        url: `${base(loanId)}/coc/${cocId}/reject`,
        method: 'POST',
        headers: getHeaders(),
        body: { reason },
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /** Raised in error. Withdrawn before review, and kept on the register. */
    /**
     * Send a finished record up for review.
     *
     * Not a status change — it stays `pending`, because it is still undecided
     * and a Closing Disclosure is still blocked while it sits. What moves is
     * ownership: the officer stops being able to edit it and a reviewer starts
     * being able to decide it. Approve and reject refuse a record that has not
     * been through here, so the ordering is the server's rule and not a habit
     * of this screen.
     */
    /**
     * Edit a pending record.
     *
     * A patch, not a replacement: send only the fields that moved. An absent
     * key leaves the column alone and an explicit null clears it, which is why
     * the caller builds the body rather than handing over whole form state.
     *
     * `cc_code` and `cc_prong` are separate fields and inseparable values —
     * the register requires a prong on code A and forbids one elsewhere, so
     * moving the code away from A must send `cc_prong: null` alongside it.
     */
    updateCoc: builder.mutation({
      query: ({ loanId, cocId, ...patch }) => ({
        url: `${base(loanId)}/coc/${cocId}`,
        method: 'PATCH',
        headers: getHeaders(),
        body: patch,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),

    submitCoc: builder.mutation({
      query: ({ loanId, cocId }) => ({
        url: `${base(loanId)}/coc/${cocId}/submit`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    withdrawCoc: builder.mutation({
      query: ({ loanId, cocId, reason }) => ({
        url: `${base(loanId)}/coc/${cocId}/withdraw`,
        method: 'POST',
        headers: getHeaders(),
        body: { reason },
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /**
     * Retire an approved record whose claim turned out to be wrong.
     *
     * No body. Only `approved` records can be superseded — a disclosed one went
     * to the borrower and nothing reaches back — and the correction is a
     * separate act, so this says only "that one was wrong".
     */
    supersedeCoc: builder.mutation({
      query: ({ loanId, cocId }) => ({
        url: `${base(loanId)}/coc/${cocId}/supersede`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
    }),

    /** Generic event entry point — backs the dev-only simulator. */
    recordDisclosureEvent: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${base(loanId)}/events`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Disclosure'],
      onQueryStarted: invalidateFeesOnSuccess,
    }),
  }),
});

/**
 * Absolute URL for a version's rendered PDF.
 *
 * `api` matters and is not decoration. Every other call here goes through
 * `baseQuery` → `apiClient`, which supplies the origin and the `/api/v1`
 * prefix. A bare path handed to `window.open` or `fetch` resolves against the
 * *frontend* origin instead, where the dev server answers with `index.html`
 * and the request never reaches the backend at all.
 */
export const disclosureDocumentUrl = (loanId, ref) =>
  `${api}${base(loanId)}/${ref}/document`;

/**
 * Fetch a rendered disclosure as a blob.
 *
 * Streamed `application/pdf`, so it cannot be an RTK query. Auth on this API is
 * HttpOnly cookies, which means `credentials: 'include'` is required — a plain
 * `window.open` sends no cookies cross-origin and gets a 401 even when the URL
 * is right. `getHeaders()` carries the CSRF token; its JSON `Content-Type` is
 * dropped because this request has no body.
 *
 * Same shape as the fees worksheet's own export (`FeesWorksheetNew.jsx`).
 */
export const fetchDisclosureDocument = async (loanId, ref) => {
  const headers = getHeaders();
  delete headers['Content-Type'];

  const response = await fetch(disclosureDocumentUrl(loanId, ref), {
    method: 'GET',
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    // The server sends a JSON envelope on failure, not a PDF. Read the reason
    // rather than handing the user a corrupt download.
    let detail = `The server returned ${response.status}.`;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* not JSON — keep the status line */
    }
    throw new Error(detail);
  }

  return response.blob();
};

export const {
  useGetDisclosureContextQuery,
  useGetAutomationQuery,
  useGetLedgerQuery,
  useGetOverviewQuery,
  useGetCocRegisterQuery,
  useGetCocRecordQuery,
  useGetCdReadinessQuery,
  useGetCocFormContextQuery,
  useGetCocPlaybookQuery,
  useGetToleranceQuery,
  useApplyToleranceCureMutation,
  useGetTridChecksQuery,
  useGetComparisonQuery,
  useGetReleasePreflightQuery,
  useGetDisclosureFormQuery,
  useGetDisclosureContactsQuery,
  useGetWorkingContactsQuery,
  useAddLoanContactMutation,
  useRemoveLoanContactMutation,
  useGetEsignStatusQuery,
  useLazyGetEsignStatusQuery,
  useSendForEsignMutation,
  useWithdrawEsignMutation,
  useAdvanceEsignMutation,
  useUpdateDisclosureContextMutation,
  useDraftInitialLeMutation,
  useDraftDisclosureMutation,
  useReleaseDisclosureMutation,
  useDiscardDisclosureMutation,
  useConfirmReceiptMutation,
  useArchiveDisclosureMutation,
  useDetectSettlementRequoteMutation,
  useDetectPricingChangeMutation,
  useRecordCocMutation,
  useAttachCocsToDraftMutation,
  useDetachCocFromDraftMutation,
  useAttachCocEvidenceMutation,
  useDetachCocEvidenceMutation,
  useApproveCocMutation,
  useRejectCocMutation,
  useSubmitCocMutation,
  useUpdateCocMutation,
  useWithdrawCocMutation,
  useSupersedeCocMutation,
  useRecordDisclosureEventMutation,
} = disclosuresAPI;
